import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import { X, MapPin, Square, Box, ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PortfolioProject } from '@/lib/portfolio-data';
import { getCachedPortfolio, fetchPortfolio, subscribeToCache } from '@/lib/globalDataCache';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';
import { Pagination } from '@/components/ui/pagination';
import { useIsMobile } from '@/hooks/use-mobile';
import { useScrollLock } from '@/hooks/use-scroll-lock';

// ─── Internal Photo Slider ─────────────────────────────────────────────────────

interface ModalSliderProps {
  images: string[]; // at least 1
  alt: string;
}

function ModalSlider({ images, alt }: ModalSliderProps) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const total = images.length;

  const goTo = useCallback((idx: number) => {
    setCurrent((idx + total) % total);
  }, [total]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (total <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % total);
    }, 10000);
  }, [total]);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resetTimer]);

  const handlePrev = () => { goTo(current - 1); resetTimer(); };
  const handleNext = () => { goTo(current + 1); resetTimer(); };

  return (
    <div className="relative w-full h-full overflow-hidden">
      <AnimatePresence mode="wait" initial={false}>
        <motion.img
          key={current}
          src={images[current]}
          alt={`${alt} ${current + 1}`}
          className="w-full h-full object-cover absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        />
      </AnimatePresence>

      {total > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full transition-colors"
            aria-label="Предыдущее фото"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full transition-colors"
            aria-label="Следующее фото"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => { goTo(i); resetTimer(); }}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-all duration-200',
                  i === current ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'
                )}
                aria-label={`Фото ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Portfolio Page ─────────────────────────────────────────────────────────────

export const Portfolio: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { t, language } = useLanguage();
  const portfolioHero = usePageHero({
    pageKey: 'portfolio',
    lang: language,
    fallbacks: {
      eyebrow: t('page.portfolio.achievements'),
      title: t('page.portfolio.title'),
    },
  });
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<PortfolioProject | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showContent, setShowContent] = useState(true); // Control animation visibility
  const [currentPage, setCurrentPage] = useState(1);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [allProjects, setAllProjects] = useState<PortfolioProject[] | null>(null);
  const loadingProjects = allProjects === null;
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchPortfolio().then((data) => { setAllProjects(data); });

    const unsub = subscribeToCache(() => {
      const updated = getCachedPortfolio();
      if (updated) setAllProjects(updated);
    });
    return unsub;
  }, []);

  // Filter by search query — uses localised fields per current language
  const projects = allProjects ?? [];
  const filteredProjects = projects.filter(project => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const lang = language as 'ru' | 'en' | 'uz';
    const cityMatch = project.city[lang]?.toLowerCase().includes(q);
    const countryMatch = project.country?.[lang]?.toLowerCase().includes(q);
    const titleMatch = project.title[lang]?.toLowerCase().includes(q);
    return cityMatch || countryMatch || titleMatch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProjects.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  const handleCardClick = (id: string, e: React.PointerEvent) => {
    // Only handle touch/coarse pointer events
    if (e.pointerType !== 'touch') return;

    // Check if the click was on the "Read More" button or its children
    const target = e.target as HTMLElement;
    if (target.closest('.details-button')) return;

    setActiveId(prevId => prevId === id ? null : id);
  };

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
    setActiveId(null); // Clear active card on search
  }, [searchQuery]);

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Handle page change with smooth scroll
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setActiveId(null); // Clear active card on page change
    
    // Smooth scroll to grid container
    if (gridContainerRef.current) {
      const headerOffset = 100; // Account for sticky header
      const elementPosition = gridContainerRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Auto-open project from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('project');
    
    if (projectId && !selectedProject) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setSelectedProject(project);
      }
    }
  }, [location.search, allProjects, selectedProject]);

  // Close modal handler - removes URL parameter
  const handleCloseModal = () => {
    setSelectedProject(null);
    // Remove the project parameter from URL
    const params = new URLSearchParams(location.search);
    params.delete('project');
    const newSearch = params.toString();
    navigate(`/${language}/portfolio${newSearch ? '?' + newSearch : ''}`, { replace: true });
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [location.search]);

  // Body scroll lock
  useScrollLock(!!selectedProject);

  useEffect(() => {
    // Only apply for mobile/tablet behavior (under 1025px)
    if (isMobile) {
      if (selectedProject) {
        document.body.style.overflow = 'hidden';
        // For mobile swipe container
        const swipeTrack = document.querySelector('.mobile-swipe-track') as HTMLElement;
        if (swipeTrack) swipeTrack.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
        const swipeTrack = document.querySelector('.mobile-swipe-track') as HTMLElement;
        if (swipeTrack) swipeTrack.style.overflow = 'auto';
      }
    }
    return () => {
      document.body.style.overflow = '';
      const swipeTrack = document.querySelector('.mobile-swipe-track') as HTMLElement;
      if (swipeTrack) swipeTrack.style.overflow = 'auto';
    };
  }, [selectedProject, isMobile]);

  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  return (
    <div className="pb-16 lg:pb-24">
      <SEO
        enabled={seoEnabled}
        title={language === 'uz' ? "SARGO portfeli | Amalga oshirilgan ob'ektlar va loyihalar" : language === 'en' ? 'SARGO Portfolio | Completed Objects and Projects' : 'Портфолио SARGO | Реализованные объекты и проекты'}
        description={language === 'uz'
          ? "SARGO amalga oshirilgan ob'ektlar portfeli: SPC laminat, SPC panellar va gilam plitka ishlatilgan tijorat va turar-joy loyihalari."
          : language === 'en'
            ? 'SARGO portfolio of completed projects: commercial and residential installations using SPC laminate, SPC panels and carpet tile.'
            : 'Портфолио реализованных объектов SARGO: коммерческие и жилые проекты с использованием SPC-ламината, SPC-панелей и ковровой плитки.'}
        canonical={getPagePath('portfolio', language as Lang)}
        schema={buildBreadcrumbs(window.location.origin, [
          { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
          { name: language === 'uz' ? 'Portfolio' : language === 'en' ? 'Portfolio' : 'Портфолио', url: getPagePath('portfolio', language as Lang) },
        ])}
      />
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-12 lg:pb-16 relative">
        <div className="grid-cells opacity-50" />
        <div className="container-custom relative z-10">
          <div className="space-y-4 lg:space-y-6">
            {portfolioHero.eyebrow?.trim() && (
              <motion.div
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={headerVariants}
                className="flex items-center gap-2 lg:gap-3"
              >
                <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{portfolioHero.eyebrow}</p>
              </motion.div>
            )}
            {portfolioHero.title?.trim() && (
              <motion.h1
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={headerVariants}
                className="font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8]"
              >
                {portfolioHero.title}<span className="text-primary">.</span>
              </motion.h1>
            )}
            {portfolioHero.subtitle?.trim() && (
              <motion.p
                custom={2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={headerVariants}
                className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider"
              >
                {portfolioHero.subtitle}
              </motion.p>
            )}

            {/* Search Input — desktop only (inside header section) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="pt-4 hidden lg:block"
            >
              <div className="relative max-w-md group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-primary transition-colors w-[18px] h-[18px]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('portfolio.search_placeholder')}
                  className="w-full bg-white/5 border border-white/10 rounded-sm py-3.5 pl-12 pr-4 text-sm uppercase tracking-wider font-bold text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:bg-white/10 transition-all"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Search Input — mobile/tablet only (scrolls with content) */}
      <div className="portfolio-search-sticky lg:hidden">
        <div className="container-custom">
          <div className="relative max-w-md mx-auto group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-primary transition-colors w-3.5 h-3.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('portfolio.search_placeholder')}
              className="portfolio-search-input w-full bg-white/5 border border-white/10 rounded-sm pl-9 pr-4 uppercase tracking-wider font-bold text-white placeholder:text-white/20 focus:outline-none focus:border-primary/40 focus:bg-white/10 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <section className="py-12 lg:py-20 relative portfolio-grid-section">
        <div className="grid-cells-accent opacity-10" />
        <div className="container-custom relative z-10">
          <div ref={gridContainerRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 lg:gap-12 min-h-[400px]">
            {/* No loading skeleton — data comes from global cache instantly */}

            {!loadingProjects && paginatedProjects.length > 0 ? (
              paginatedProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ 
                    opacity: showContent ? 1 : 0, 
                    y: showContent ? 0 : 30,
                  }}
                  transition={{
                    duration: 0.4,
                    delay: showContent ? index * 0.1 : 0,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  className={cn(
                    "mirror-card group flex flex-row md:flex-col xl:flex-row h-full min-h-[160px] max-h-[200px] md:max-h-[520px] md:min-h-[520px] xl:max-h-[360px] xl:min-h-[360px] overflow-hidden",
                    activeId === project.id && "is-active"
                  )}
                  onPointerUp={(e) => handleCardClick(project.id, e)}
                >
                  <div className="w-[42%] md:w-full xl:w-1/2 portfolio-image-wrapper md:h-[280px] xl:h-auto min-h-0 overflow-hidden flex-shrink-0">
                    <img
                      src={project.image}
                      alt={project.title[language as keyof typeof project.title]}
                      loading="lazy"
                      className="w-full h-full object-cover md:grayscale md:group-hover:grayscale-0 md:group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="portfolio-grayscale-overlay" />
                    <div className="absolute inset-0 bg-primary/10 opacity-0 md:group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="mirror-reflection opacity-20 md:group-hover:opacity-40 transition-opacity duration-500" />
                    <div className="shine-overlay md:group-hover:opacity-100 transition-opacity duration-700" />
                  </div>
                  <div className="w-[55%] md:w-full xl:w-1/2 p-4 md:p-6 lg:p-8 flex flex-col justify-between portfolio-card-content">
                    <div className="space-y-3 md:space-y-6 portfolio-card-content-inner">
                      <h3 className="text-sm md:text-xl lg:text-2xl font-bold uppercase tracking-tight group-hover:text-primary transition-colors leading-tight line-clamp-2 md:line-clamp-none">
                        {project.title[language as keyof typeof project.title]}
                      </h3>

                      <div className="space-y-2 md:space-y-4">
                        <div className="flex justify-between items-center text-[7px] md:text-[9px] lg:text-[10px] uppercase tracking-widest font-bold">
                          <span className="text-white/30 flex items-center gap-1.5 md:gap-2"><MapPin size={10} className="md:w-3 md:h-3" /> {t('portfolio.city_label')}</span>
                          <span className="text-white/80">{project.city[language as keyof typeof project.city]}</span>
                        </div>
                        <div className="flex justify-between items-center text-[7px] md:text-[9px] lg:text-[10px] uppercase tracking-widest font-bold">
                          <span className="text-white/30 flex items-center gap-1.5 md:gap-2"><Square size={10} className="md:w-3 md:h-3" /> {t('portfolio.area_label')}</span>
                          <span className="text-white/80">{project.area}</span>
                        </div>
                        <div className="flex justify-between items-center text-[7px] md:text-[9px] lg:text-[10px] uppercase tracking-widest font-bold">
                          <span className="text-white/30 flex items-center gap-1.5 md:gap-2"><Box size={10} className="md:w-3 md:h-3" /> {t('portfolio.product_label')}</span>
                          <span className="text-white/80 line-clamp-1">{project.product[language as keyof typeof project.product]}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedProject(project)}
                      className="details-button mt-4 md:mt-8 flex items-center gap-2 md:gap-3 text-[8px] md:text-[10px] lg:text-xs font-bold uppercase tracking-widest text-primary hover:gap-5 transition-all duration-300 min-h-0 md:min-h-[44px]"
                    >
                      {t('portfolio.read_more')} <Plus size={14} className="md:w-4 md:h-4" />
                    </button>
                  </div>
                </motion.div>
              ))
            ) : !loadingProjects ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: showContent ? 1 : 0 }}
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                className="col-span-full py-20 text-center"
              >
                <p className="text-white/40 uppercase tracking-[0.2em] font-bold text-sm lg:text-base">
                  {t('portfolio.empty')}
                </p>
              </motion.div>
            ) : null}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 lg:mt-16">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </section>

      {/* Modal - Portal for correct positioning on mobile */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {selectedProject && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 lg:p-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseModal}
                className="absolute inset-0 bg-[#13100B]/85 backdrop-blur-xl"
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.98, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className={cn(
                  "relative bg-background border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col md:flex-row mirror-style",
                  isMobile 
                    ? "w-[92%] h-[70vh] rounded-[18px] overflow-hidden" 
                    : "w-full md:w-full md:max-w-4xl md:h-auto max-h-[calc(100vh-80px)] md:rounded-none overflow-y-auto overscroll-contain"
                )}
              >
                {/* Close Button - Sticky on Mobile */}
                <button
                  onClick={handleCloseModal}
                  className="absolute top-4 right-4 z-[70] w-10 h-10 flex items-center justify-center bg-background/50 backdrop-blur-md border border-white/10 text-white hover:bg-primary transition-all duration-300 rounded-full md:rounded-sm"
                >
                  <X size={20} />
                </button>

                <div className="w-full md:w-5/12 relative h-48 sm:h-64 md:h-auto overflow-hidden border-b md:border-b-0 md:border-r border-white/5 flex-shrink-0">
                  <ModalSlider
                    images={
                      selectedProject.internalImages && selectedProject.internalImages.length > 0
                        ? selectedProject.internalImages
                        : [selectedProject.image]
                    }
                    alt={selectedProject.title[language as keyof typeof selectedProject.title]}
                  />
                </div>

                <div className={cn(
                  "w-full md:w-7/12 p-6 lg:p-10 no-scrollbar space-y-6 lg:space-y-8 bg-background/40 backdrop-blur-sm",
                  isMobile ? "overflow-y-auto" : "overflow-visible"
                )}>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px_rgba(176,138,90,0.8)]"></div>
                      <div className="text-primary text-[9px] font-black uppercase tracking-[0.3em]">{t('portfolio.modal.details_label')}</div>
                    </div>
                    <h2 className="text-xl lg:text-3xl font-black uppercase tracking-tight leading-none">
                      {selectedProject.title[language as keyof typeof selectedProject.title]}
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 lg:gap-y-6">
                     <div className="space-y-1 border-l-2 border-primary/20 pl-4 py-1">
                      <div className="text-[9px] uppercase tracking-widest font-black text-white/20">
                        {t('portfolio.city_label')}
                      </div>
                      <div className="text-[10px] lg:text-xs font-black text-white/80">{selectedProject.city[language as keyof typeof selectedProject.city]}</div>
                    </div>
                    <div className="space-y-1 border-l-2 border-primary/20 pl-4 py-1">
                      <div className="text-[9px] uppercase tracking-widest font-black text-white/20">
                        {t('portfolio.area_label')}
                      </div>
                      <div className="text-[10px] lg:text-xs font-black text-white/80">{selectedProject.area}</div>
                    </div>
                    <div className="space-y-1 border-l-2 border-primary/20 pl-4 py-1">
                      <div className="text-[9px] uppercase tracking-widest font-black text-white/20">
                        {t('portfolio.product_label')}
                      </div>
                      <div className="text-[10px] lg:text-xs font-black text-white/80 line-clamp-1">{selectedProject.product[language as keyof typeof selectedProject.product]}</div>
                    </div>
                    {selectedProject.partner && (
                      <div className="space-y-1 border-l-2 border-primary/20 pl-4 py-1">
                        <div className="text-[9px] uppercase tracking-widest font-black text-white/20">
                          {t('portfolio.partner_label')}
                        </div>
                        <div className="text-[10px] lg:text-xs font-black text-white/80 line-clamp-1">{selectedProject.partner}</div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                     <div className="text-[9px] uppercase tracking-widest font-bold text-white/20">{t('portfolio.modal.description_label')}</div>
                     <p className="text-white/40 leading-relaxed text-[11px] lg:text-xs">
                       {selectedProject.description[language as keyof typeof selectedProject.description]}
                     </p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};
